// ============================================================
// useMoveSwap — 予約移動・入替・車両スワップロジック
// ============================================================

import { useState, useCallback, useRef, useMemo } from 'react';
import { Reservation, Vehicle } from './types';
// @ts-ignore
import { saveDispatchReservation } from '../../api/dispatchService';
// @ts-ignore
import { reservationSyncService } from '../../services/reservationSyncService';

/** メモにアクションログを追記 */
export function appendMemoLog(reservation: Reservation, action: string, detail: string): Reservation {
  const now = new Date();
  const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  let staffName = 'システム';
  if (typeof window !== 'undefined') {
    if ((window as any).bo_staff?.name) {
      staffName = (window as any).bo_staff.name;
    } else {
      try {
        const saved = localStorage.getItem('bo_staff');
        if (saved) staffName = JSON.parse(saved).name || 'システム';
      } catch { /* ignore */ }
    }
  }

  const header = `---${action} ${staffName} ${ts}---`;
  const entry = detail ? `${header}\n${detail}\n---` : `${header}\n---`;
  const currentMemo = (reservation as any).memo || '';
  return { ...reservation, memo: currentMemo ? `${currentMemo}\n${entry}` : entry } as Reservation;
}

function formatDateShort(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** キャッシュを直接パッチして即座にUIに反映（楽観的更新） */
function patchCacheOptimistic(updates: Reservation[]) {
  for (const r of updates) {
    reservationSyncService.cachedReservations.set(String(r.id), r);
  }
  const all = reservationSyncService.getReservations();
  reservationSyncService.listeners.forEach((cb: Function) => cb([], all));
}

export type InteractionMode = 'none' | 'move' | 'move-free' | 'swap' | 'vehicle-swap';

interface UndoAction {
  label: string;
  execute: () => Promise<void>;
}

const WARNING_KEYWORDS = ['車検', '点検', '修理', 'タイヤ', '指定'];

export interface VehicleSwapInfo {
  vehicleA: Vehicle;
  vehicleB: Vehicle;
  /** 入替対象の予約（未来 + 進行中でない） */
  swapTargetsA: Reservation[];
  swapTargetsB: Reservation[];
  /** 警告予約（キーワードに一致する入替対象） */
  warningsA: Reservation[];
  warningsB: Reservation[];
  /** 除外される予約（過去 or 進行中 in_use/done） */
  excludedA: Reservation[];
  excludedB: Reservation[];
}

interface UseMoveSwapProps {
  reservations: Reservation[];
  vehicles: Vehicle[];
  onComplete: () => void;
  onToast?: (message: string, severity: 'success' | 'error') => void;
}

interface UseMoveSwapReturn {
  mode: InteractionMode;
  selectedReservation: Reservation | null;
  enterMove: () => void;
  enterSwap: () => void;
  enterVehicleSwap: () => void;
  startMove: (r: Reservation, freeMove?: boolean) => void;
  startSwap: (r: Reservation) => void;
  executeMove: (targetVehicleId: string, targetDate: Date) => Promise<void>;
  executeSwap: (targetReservation: Reservation) => Promise<void>;
  cancel: () => void;
  undoAction: UndoAction | null;
  dismissUndo: () => void;
  // vehicle-swap
  vehicleSwapSource: Vehicle | null;
  vehicleSwapInfo: VehicleSwapInfo | null;
  selectVehicleSwapSource: (v: Vehicle) => void;
  selectVehicleSwapTarget: (v: Vehicle) => void;
  executeVehicleSwap: () => Promise<void>;
  cancelVehicleSwap: () => void;
}

export function useMoveSwap({ reservations, vehicles, onComplete, onToast }: UseMoveSwapProps): UseMoveSwapReturn {
  const [mode, setMode] = useState<InteractionMode>('none');
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [undoAction, setUndoAction] = useState<UndoAction | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // vehicle-swap state
  const [vehicleSwapSource, setVehicleSwapSource] = useState<Vehicle | null>(null);
  const [vehicleSwapInfo, setVehicleSwapInfo] = useState<VehicleSwapInfo | null>(null);

  const scheduleUndoDismiss = useCallback((action: UndoAction) => {
    setUndoAction(action);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => setUndoAction(null), 5000);
  }, []);

  const enterMove = useCallback(() => {
    setMode('move');
    setSelectedReservation(null);
  }, []);

  const enterSwap = useCallback(() => {
    setMode('swap');
    setSelectedReservation(null);
  }, []);

  const enterVehicleSwap = useCallback(() => {
    setMode('vehicle-swap');
    setSelectedReservation(null);
    setVehicleSwapSource(null);
    setVehicleSwapInfo(null);
  }, []);

  const startMove = useCallback((r: Reservation, freeMove?: boolean) => {
    setMode(freeMove ? 'move-free' : 'move');
    setSelectedReservation(r);
  }, []);

  const startSwap = useCallback((r: Reservation) => {
    setMode('swap');
    setSelectedReservation(r);
  }, []);

  const cancel = useCallback(() => {
    setMode('none');
    setSelectedReservation(null);
    setVehicleSwapSource(null);
    setVehicleSwapInfo(null);
  }, []);

  const dismissUndo = useCallback(() => {
    setUndoAction(null);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, []);

  // ========== 予約移動 ==========
  const executeMove = useCallback(async (targetVehicleId: string, targetDate: Date) => {
    if (!selectedReservation) return;

    // 同じ車両 & 同じ日時への移動は no-op（スキップ）
    const sameVehicle = String(selectedReservation.vehicle_id) === String(targetVehicleId);
    if (mode !== 'move-free' && sameVehicle) {
      setMode('none');
      setSelectedReservation(null);
      return;
    }
    if (mode === 'move-free' && sameVehicle) {
      const origStart = new Date(selectedReservation.start_date);
      if (Math.abs(origStart.getTime() - targetDate.getTime()) < 60000) {
        setMode('none');
        setSelectedReservation(null);
        return;
      }
    }

    const original = { ...selectedReservation };
    let updated = { ...selectedReservation, vehicle_id: targetVehicleId };

    if (mode === 'move-free') {
      const origStart = new Date(selectedReservation.start_date);
      const origEnd = new Date(selectedReservation.end_date);
      const duration = origEnd.getTime() - origStart.getTime();
      updated.start_date = targetDate.toISOString();
      updated.end_date = new Date(targetDate.getTime() + duration).toISOString();
    }

    // メモ追記
    const fromVehicle = vehicles.find(v => String(v.id) === String(original.vehicle_id));
    const toVehicle = vehicles.find(v => String(v.id) === targetVehicleId);
    const fromCode = fromVehicle?.vehicle_code || original.vehicle_id;
    const toCode = toVehicle?.vehicle_code || targetVehicleId;
    if (mode === 'move-free') {
      updated = appendMemoLog(updated, '予約移動（自由）', `${fromCode} → ${toCode}\n日時: ${formatDateShort(targetDate)}`);
    } else {
      updated = appendMemoLog(updated, '予約移動', `${fromCode} → ${toCode}`);
    }

    patchCacheOptimistic([updated]);
    setMode('none');
    setSelectedReservation(null);

    scheduleUndoDismiss({
      label: '移動を元に戻す',
      execute: async () => {
        patchCacheOptimistic([original]);
        await saveDispatchReservation(original);
        // Pusher でキャッシュ即時更新されるため forceSync 不要
      },
    });

    try {
      await saveDispatchReservation(updated);
      // Pusher でキャッシュ即時更新されるため forceSync 不要
      onToast?.('移動が完了しました', 'success');
    } catch (e) {
      patchCacheOptimistic([original]);
      console.error('[useMoveSwap] move failed, reverted:', e);
      onToast?.('移動に失敗しました', 'error');
    }
  }, [selectedReservation, mode, scheduleUndoDismiss, onToast, vehicles]);

  // ========== 予約入替（2予約の vehicle_id 交換） ==========
  const executeSwap = useCallback(async (targetReservation: Reservation) => {
    if (!selectedReservation) return;
    const r1 = selectedReservation;
    const r2 = targetReservation;
    if (r1.id === r2.id) { cancel(); return; }

    const v1 = vehicles.find(v => String(v.id) === String(r1.vehicle_id));
    const v2 = vehicles.find(v => String(v.id) === String(r2.vehicle_id));
    const code1 = v1?.vehicle_code || r1.vehicle_id;
    const code2 = v2?.vehicle_code || r2.vehicle_id;

    let r1New = appendMemoLog({ ...r1, vehicle_id: r2.vehicle_id }, '予約入替', `${code1} → ${code2}（${r2.customer_name || '予約'}と入替）`);
    let r2New = appendMemoLog({ ...r2, vehicle_id: r1.vehicle_id }, '予約入替', `${code2} → ${code1}（${r1.customer_name || '予約'}と入替）`);

    patchCacheOptimistic([r1New, r2New]);
    setMode('none');
    setSelectedReservation(null);

    scheduleUndoDismiss({
      label: '入替を元に戻す',
      execute: async () => {
        patchCacheOptimistic([r1, r2]);
        await Promise.all([saveDispatchReservation(r1), saveDispatchReservation(r2)]);
        // Pusher でキャッシュ即時更新されるため forceSync 不要
      },
    });

    try {
      await Promise.all([saveDispatchReservation(r1New), saveDispatchReservation(r2New)]);
      // Pusher でキャッシュ即時更新されるため forceSync 不要
      onToast?.('入替が完了しました', 'success');
    } catch (e) {
      patchCacheOptimistic([r1, r2]);
      console.error('[useMoveSwap] swap failed, reverted:', e);
      onToast?.('入替に失敗しました', 'error');
    }
  }, [selectedReservation, cancel, scheduleUndoDismiss, onToast, vehicles]);

  // ========== 車両スワップ ==========

  /** 予約を入替対象 / 除外に分類 */
  const classifyReservations = useCallback((vehicleId: string): {
    targets: Reservation[];
    warnings: Reservation[];
    excluded: Reservation[];
  } => {
    const now = new Date();
    const targets: Reservation[] = [];
    const warnings: Reservation[] = [];
    const excluded: Reservation[] = [];

    for (const r of reservations) {
      if (String(r.vehicle_id) !== vehicleId) continue;
      const start = new Date(r.start_date);
      const end = new Date(r.end_date);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) continue;

      // 過去の予約（返却日時が現在より前）→ 除外
      if (end < now) {
        excluded.push(r);
        continue;
      }

      // 現在進行中（貸出日時 < 現在 < 返却日時）で in_use or done → 除外
      if (start < now && end > now) {
        const statusCode = String(r.status);
        if (statusCode === 'in_use' || statusCode === 'done' ||
            statusCode === '3' || statusCode === '4') {
          excluded.push(r);
          continue;
        }
      }

      // 入替対象
      targets.push(r);

      // 警告キーワードチェック
      const text = `${r.customer_name || ''} ${(r as any).memo || ''} ${(r as any).comment || ''} ${(r as any).short_memo || ''}`;
      if (WARNING_KEYWORDS.some(kw => text.includes(kw))) {
        warnings.push(r);
      }
    }

    return { targets, warnings, excluded };
  }, [reservations]);

  /** 車両スワップ: ソース選択 */
  const selectVehicleSwapSource = useCallback((v: Vehicle) => {
    setVehicleSwapSource(v);
  }, []);

  /** 車両スワップ: ターゲット選択 → 確認情報を生成 */
  const selectVehicleSwapTarget = useCallback((v: Vehicle) => {
    if (!vehicleSwapSource) return;
    if (String(v.id) === String(vehicleSwapSource.id)) return;

    const classA = classifyReservations(String(vehicleSwapSource.id));
    const classB = classifyReservations(String(v.id));

    setVehicleSwapInfo({
      vehicleA: vehicleSwapSource,
      vehicleB: v,
      swapTargetsA: classA.targets,
      swapTargetsB: classB.targets,
      warningsA: classA.warnings,
      warningsB: classB.warnings,
      excludedA: classA.excluded,
      excludedB: classB.excluded,
    });
  }, [vehicleSwapSource, classifyReservations]);

  /** 車両スワップ: 実行 */
  const executeVehicleSwap = useCallback(async () => {
    if (!vehicleSwapInfo) return;
    const { vehicleA, vehicleB, swapTargetsA, swapTargetsB } = vehicleSwapInfo;

    // 楽観的更新: A の予約 → B の vehicle_id, B の予約 → A の vehicle_id（メモ追記）
    const originals = [...swapTargetsA, ...swapTargetsB].map(r => ({ ...r }));
    const codeA = vehicleA.vehicle_code || String(vehicleA.id);
    const codeB = vehicleB.vehicle_code || String(vehicleB.id);
    const updatedA = swapTargetsA.map(r => appendMemoLog({ ...r, vehicle_id: String(vehicleB.id) }, '車両入替', `${codeA} → ${codeB}`));
    const updatedB = swapTargetsB.map(r => appendMemoLog({ ...r, vehicle_id: String(vehicleA.id) }, '車両入替', `${codeB} → ${codeA}`));
    const allUpdated = [...updatedA, ...updatedB];

    patchCacheOptimistic(allUpdated);
    setVehicleSwapInfo(null);
    setVehicleSwapSource(null);
    setMode('none');

    const totalCount = allUpdated.length;
    onToast?.(`${totalCount}件の予約を入れ替え中...`, 'success');

    try {
      // 並列で保存（5件ずつバッチ）
      const BATCH_SIZE = 5;
      for (let i = 0; i < allUpdated.length; i += BATCH_SIZE) {
        const batch = allUpdated.slice(i, i + BATCH_SIZE);
        await Promise.all(batch.map(r => saveDispatchReservation(r)));
      }
      // Pusher でキャッシュ即時更新されるため forceSync 不要
      onToast?.(`車両入替が完了しました（${totalCount}件）`, 'success');
    } catch (e) {
      // ロールバック
      patchCacheOptimistic(originals);
      console.error('[useMoveSwap] vehicle swap failed, reverted:', e);
      onToast?.('車両入替に失敗しました', 'error');
    }
  }, [vehicleSwapInfo, onToast]);

  const cancelVehicleSwap = useCallback(() => {
    setVehicleSwapInfo(null);
    setVehicleSwapSource(null);
    setMode('none');
  }, []);

  return {
    mode,
    selectedReservation,
    enterMove,
    enterSwap,
    enterVehicleSwap,
    startMove,
    startSwap,
    executeMove,
    executeSwap,
    cancel,
    undoAction,
    dismissUndo,
    vehicleSwapSource,
    vehicleSwapInfo,
    selectVehicleSwapSource,
    selectVehicleSwapTarget,
    executeVehicleSwap,
    cancelVehicleSwap,
  };
}
